// Cookie helper functions to get auth data

export const getCookie = (name: string): string | undefined => {
  if (typeof document === 'undefined') return undefined;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift();
  return undefined;
};

export interface AuthUserData {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  role: string;
  tenant: {
    id: string;
    name: string;
    slug: string;
  };
}

export interface AuthData {
  user: AuthUserData;
  userType: 'user' | 'client';
}

export const getCurrentUser = (): AuthData | null => {
  const userStr = getCookie('auth_user');
  const userType = getCookie('auth_user_type');
  
  if (userStr && userType) {
    try {
      return {
        user: JSON.parse(decodeURIComponent(userStr)),
        userType: userType as 'user' | 'client'
      };
    } catch (error) {
      return null;
    }
  }
  return null;
};

export const getClientId = (): string | null => {
  const authData = getCurrentUser();
  if (authData?.userType === 'client') {
    return authData.user.id;
  }
  return null;
};

export const getTenantSlug = (): string | null => {
  const authData = getCurrentUser();
  if (authData?.user?.tenant?.slug) {
    return authData.user.tenant.slug;
  }
  if (typeof document !== 'undefined') {
    const slugCookie = getCookie('tenant_slug');
    if (slugCookie) return slugCookie;
  }
  if (typeof window !== 'undefined') {
    const lastSlug = localStorage.getItem('last_tenant_slug');
    if (lastSlug) return lastSlug;
  }
  return null;
};

export const getTenantId = (): string | null => {
  const authData = getCurrentUser();
  if (authData?.user?.tenant?.id) {
    return authData.user.tenant.id;
  }
  if (typeof document !== 'undefined') {
    const idCookie = getCookie('tenant_id');
    if (idCookie) return idCookie;
  }
  return null;
};

/**
 * Extracts the brokerage/tenant name from the current browser hostname.
 * Reads from the ROOT domain (2nd-to-last label), NOT the subdomain.
 *
 * Examples:
 *  - devlegacyglobalbank.legacyglobalbank.com  -> 'legacyglobalbank'
 *  - clients.brokeragename.com                  -> 'brokeragename'
 *  - brokeragename.com                          -> 'brokeragename'
 *  - acme.localhost                              -> 'acme'
 *  - localhost                                   -> ''
 */
export const getBrokerageNameFromHostname = (): string => {
  if (typeof window === 'undefined') return '';
  const hostname = window.location.hostname.toLowerCase();
  if (!hostname || hostname === 'localhost') return '';

  const parts = hostname.split('.');

  // Handle .localhost domains (e.g. acme.localhost)
  if (hostname.endsWith('.localhost')) {
    return parts.length >= 2 ? parts[parts.length - 2] : '';
  }

  // Multi-part public suffixes (e.g. co.uk, com.au)
  const twoPartSuffixes = ['co.uk', 'com.au', 'co.in', 'org.uk', 'net.au', 'co.za', 'com.br', 'com.sg', 'com.tr'];
  const lastTwo = parts.slice(-2).join('.');
  if (twoPartSuffixes.includes(lastTwo) && parts.length >= 3) {
    // e.g. clients.brokerage.co.uk -> 'brokerage'
    return parts[parts.length - 3];
  }

  if (parts.length === 1) return parts[0]; // bare hostname
  // e.g. devlegacyglobalbank.legacyglobalbank.com -> parts[-2] = 'legacyglobalbank'
  return parts[parts.length - 2];
};

/**
 * Safely decodes a JWT payload without verifying the signature.
 */
export const getJwtPayload = (token?: string | null): any => {
  if (!token) return null;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const jsonStr =
      typeof atob !== 'undefined'
        ? decodeURIComponent(
            atob(padded)
              .split('')
              .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
              .join('')
          )
        : Buffer.from(padded, 'base64').toString('utf-8');
    return JSON.parse(jsonStr);
  } catch {
    return null;
  }
};

/**
 * Checks if a JWT token is expired (includes a 10s clock skew buffer).
 */
export const isJwtExpired = (token?: string | null): boolean => {
  if (!token) return true;
  const payload = getJwtPayload(token);
  if (!payload || !payload.exp) return false;
  return Date.now() >= payload.exp * 1000 - 10000;
};

export const setCookie = (name: string, value: string, days: number = 7): void => {
  if (typeof document === 'undefined') return;
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  const isSecure = typeof window !== 'undefined' && window.location.protocol === 'https:';
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax${isSecure ? ';Secure' : ''}`;
};

export const deleteCookie = (name: string): void => {
  if (typeof document === 'undefined') return;
  const isSecure = typeof window !== 'undefined' && window.location.protocol === 'https:';
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;SameSite=Lax${isSecure ? ';Secure' : ''}`;
  document.cookie = `${name}=;max-age=0;path=/;`;
};

export interface AdminSessionBackup {
  token: string;
  user: string;
  userType: string;
  role: string;
  tenantSlug: string;
  tenantId: string;
}

export const backupAdminSession = (): AdminSessionBackup | null => {
  if (typeof window === 'undefined') return null;
  const token = getCookie('auth_token') || '';
  const user = getCookie('auth_user') || '';
  const userType = getCookie('auth_user_type') || 'user';
  const role = getCookie('client_role') || 'admin';
  const tenantSlug = getCookie('tenant_slug') || '';
  const tenantId = getCookie('tenant_id') || '';

  if (!token || userType === 'client') {
    return null;
  }

  const backup: AdminSessionBackup = {
    token,
    user,
    userType,
    role,
    tenantSlug,
    tenantId
  };

  try {
    localStorage.setItem('admin_session_backup', JSON.stringify(backup));
    sessionStorage.setItem('admin_session_backup', JSON.stringify(backup));
  } catch (e) {
    console.error('Failed to store admin session backup:', e);
  }

  return backup;
};

export const restoreAdminSessionAndReturn = (customRedirectUrl: string = '/admin/clients') => {
  if (typeof window === 'undefined') return;

  // 1. Remove all client / force login session keys from sessionStorage
  sessionStorage.removeItem('force_auth_token');
  sessionStorage.removeItem('force_auth_user');
  sessionStorage.removeItem('force_auth_user_type');
  sessionStorage.removeItem('force_auth_session_id');
  sessionStorage.removeItem('force_auth_admin');
  sessionStorage.removeItem('force_auth_started_at');

  // 2. Clear force login and gate cookies with both expires and max-age
  deleteCookie('force_auth');
  deleteCookie('client_access_gate');
  document.cookie = 'force_auth=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;SameSite=Lax';
  document.cookie = 'client_access_gate=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;SameSite=Lax';
  document.cookie = 'force_auth=;max-age=0;path=/;';
  document.cookie = 'client_access_gate=;max-age=0;path=/;';

  // 3. Restore staff credentials from backup
  let restored = false;
  try {
    const backupStr = sessionStorage.getItem('admin_session_backup') || localStorage.getItem('admin_session_backup');
    if (backupStr) {
      const backup = JSON.parse(backupStr);
      if (backup.token && !isJwtExpired(backup.token)) {
        setCookie('auth_token', backup.token, 7);
        if (backup.user) {
          let userValue = backup.user;
          if (typeof userValue === 'object') {
            userValue = encodeURIComponent(JSON.stringify(userValue));
          } else if (typeof userValue === 'string' && !userValue.includes('%')) {
            userValue = encodeURIComponent(userValue);
          }
          setCookie('auth_user', userValue, 7);
        }
        if (backup.userType) {
          setCookie('auth_user_type', backup.userType, 7);
        }
        if (backup.role) {
          setCookie('client_role', backup.role, 7);
        }
        if (backup.tenantSlug) {
          setCookie('tenant_slug', backup.tenantSlug, 7);
        }
        if (backup.tenantId) {
          setCookie('tenant_id', backup.tenantId, 7);
        }
        restored = true;
      }
    }
  } catch (err) {
    console.error('[ReturnToAdmin] Error restoring admin session:', err);
  }

  // If no backup was found or backup was expired, check current cookies:
  // If current cookie is client, delete client auth cookies to prevent redirect back to client portal
  if (!restored) {
    const currentRole = getCookie('client_role');
    const currentUserType = getCookie('auth_user_type');
    if (currentUserType === 'client' || currentRole === 'client') {
      deleteCookie('auth_token');
      deleteCookie('client_role');
      deleteCookie('auth_user');
      deleteCookie('auth_user_type');
    }
  }

  // 4. Return to admin panel
  let closedTab = false;
  if (window.opener && !window.opener.closed) {
    try {
      window.opener.focus();
      try {
        window.opener.location.href = customRedirectUrl;
      } catch (e) {}
      window.close();
      closedTab = true;
    } catch (e) {
      console.warn('Could not focus opener window:', e);
    }
  }

  if (!closedTab) {
    window.location.href = customRedirectUrl;
  }
};

